// #452: client helpers for the cart sold-out reconciliation. The cart sync
// boundary fetches the live sold-out key set, drops matching lines on load,
// and records the removed variety names here so the cart page can surface a
// dismissible "X was sold out and removed" notice. Session-scoped (survives
// the reload that shows the cart, cleared on dismiss).

"use client";

const NOTICE_KEY = "mag-portal-sold-out-removed";
export const SOLD_OUT_NOTICE_EVENT = "mag-portal-sold-out-notice";

// Fetch the active sold-out keys from the public feed. Fails open (empty set)
// so an availability outage never blocks cart hydration - the server order
// gate is the authoritative safety net.
export async function fetchSoldOutKeys(): Promise<Set<string>> {
  try {
    const res = await fetch("/api/availability/sold-out", {
      headers: { accept: "application/json" },
    });
    if (!res.ok) return new Set();
    const data = (await res.json()) as { soldOutKeys?: string[] };
    return new Set(data.soldOutKeys ?? []);
  } catch {
    return new Set();
  }
}

export function writeSoldOutNotice(names: string[]): void {
  if (typeof window === "undefined" || names.length === 0) return;
  try {
    sessionStorage.setItem(NOTICE_KEY, JSON.stringify(names));
  } catch {
    // sessionStorage unavailable (private mode) - skip the notice silently.
  }
  window.dispatchEvent(new CustomEvent(SOLD_OUT_NOTICE_EVENT));
}

export function readSoldOutNotice(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(NOTICE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

export function clearSoldOutNotice(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(NOTICE_KEY);
  } catch {
    // ignore
  }
  window.dispatchEvent(new CustomEvent(SOLD_OUT_NOTICE_EVENT));
}
